import type { CalculationItemNavData } from '@gql/graphql';
import { useProjectParams } from '@hooks/useProjectParams';
import { ComponentWithTooltip } from 'components/ComponentWithTooltip';
import { KebabTreeCalculation } from 'components/KebabTreeCalculation/KebabTreeCalculation';
import { useProjectData } from 'hooks/useProjectData';
import { cnMixSpace, IconAlert } from 'uikit';

import { F as EmptyFn } from 'ramda';
import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { isCostRoundingOrSeasonsChanged } from '../utils';

import type { NavItems } from './constants';
import { ProjectTreeLeaf } from './ProjectTreeLeaf';
import { useCheckActiveTreeItem } from './useCheckActiveTreeItem';

type TCalculationTree = {
    getContractNavItemUrl: ({
        baseUrl,
        itemId,
        navLinkId,
    }: {
        baseUrl: string;
        navLinkId: string;
        itemId: number;
    }) => string;
};

export const CalculationTree = ({ getContractNavItemUrl }: TCalculationTree) => {
    const { id, methodology } = useProjectParams();
    const navigate = useNavigate();
    const { projectData } = useProjectData(id, false);

    const { hasNavigation, serviceInfo, calculationNavigationData } = useMemo(
        () => ({
            hasNavigation: !!projectData?.calculationNavigation,
            serviceInfo: projectData?.calculationNavigation,
            calculationNavigationData: projectData?.calculationNavigation?.data,
        }),
        [projectData?.calculationNavigation],
    );

    const onCalculationClick = useCallback(() => {
        if (projectData?.calculationId) {
            navigate(`/projects/${methodology}/${id}/calculation`);
        } else {
            navigate(`/projects/${methodology}/${id}/calculation/edit`);
        }
    }, [projectData?.calculationId, navigate, methodology, id]);

    const checkEntityIsActive = useCheckActiveTreeItem();

    const showChangeInformer = useCallback(
        (navItem: CalculationItemNavData) => {
            const { isSeasonsChanged, isCostRoundingChanged, isObjectStructureChanged } =
                isCostRoundingOrSeasonsChanged(
                    navItem.navLinkId as NavItems,
                    calculationNavigationData?.calculationNavData,
                );

            return isSeasonsChanged || isCostRoundingChanged || isObjectStructureChanged;
        },
        [calculationNavigationData?.calculationNavData],
    );

    if (!hasNavigation) {
        return null;
    }

    return (
        <>
            {!serviceInfo?.result && (
                <ComponentWithTooltip tooltipText={serviceInfo?.message}>
                    <ProjectTreeLeaf
                        label={
                            <span className="serviceMsgNode">
                                <IconAlert view="alert" className={cnMixSpace({ mR: '2xs' })} />
                                Расчет НМЦ
                            </span>
                        }
                        horizontalSpace="m"
                        onClick={EmptyFn}
                        boldStyle
                    />
                </ComponentWithTooltip>
            )}

            {calculationNavigationData && (
                <ProjectTreeLeaf
                    label={calculationNavigationData?.calculationNavData?.name}
                    horizontalSpace="m"
                    onClick={onCalculationClick}
                    boldStyle
                    isActiveLink={checkEntityIsActive({
                        entityName: 'calculation',
                        entityId: ['edit'],
                    })}
                    rightSide={<KebabTreeCalculation />}
                />
            )}
            {calculationNavigationData?.calculationNavItems?.map((item) => {
                const baseUrl = `/projects/${methodology}/${id}`;
                const { id: itemId, navLinkId } = item;

                const urlTo = getContractNavItemUrl({
                    itemId,
                    navLinkId,
                    baseUrl,
                });

                return (
                    <ProjectTreeLeaf
                        key={item.name}
                        label={item.name}
                        to={urlTo}
                        horizontalSpace="2xl"
                        isActiveLink={
                            urlTo
                                ? checkEntityIsActive({
                                      entityName: urlTo,
                                      entityId: '',
                                  })
                                : false
                        }
                        rightSide={showChangeInformer(item) && <IconAlert size="s" view="warning" />}
                    />
                );
            })}
        </>
    );
};

CalculationTree.displayName = 'CalculationTree';
